import React, { useState, useEffect, createRef } from "react";
import { usePlant } from "../../components/PlantHook";
import FullCalendar, { EventInput } from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrap from "@fullcalendar/bootstrap";
import { Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import ActiveIconWithCopy from "../../svg/ActiveIconWithCopy";
import { OverrideDate, ProductionSiteCalendar } from "../models/ProductionSiteCalendarModel";
import * as actions from "../redux/ProductionSiteCalendarActions";
import moment from "moment";
import OverrideDateEditModal from "../components/OverrideDateEditModal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { DetailedAlert } from "../../utils/DetailedAlert";

const ProductionSiteCalendarPage = () => {
  const [events, setEvents] = useState([] as EventInput[]);
  const [editOverride, setEditOverride] = useState<OverrideDate | undefined>(undefined);
  const [weekdaysLocked, setWeekdaysLocked] = useState(true);
  const calendarRef = createRef<FullCalendar>();
  const plantCode = usePlant();

  const dispatch = useDispatch();

  const state = useSelector(
    (state: any) => ({
      alert: state.productionSiteCalendar.alert || null,
      nrApiCallsInProgress: state.productionSiteCalendar.nrApiCallsInProgress,
      productionSiteCalendar: state.productionSiteCalendar.productionSiteCalendar,
    }),
    shallowEqual
  );
  const alert = state.alert as DetailedAlert
  const numberOfApiCallsInProgress = state.nrApiCallsInProgress as number;
  const productionSiteCalendar = state.productionSiteCalendar as ProductionSiteCalendar

  useEffect(() => {
    dispatch(actions.fetchProductionSiteCalendar(plantCode));
  }, [dispatch, plantCode]);

  useEffect(() => {
    if (productionSiteCalendar) {
      setEvents(productionSiteCalendar.overrideDates.map(getEvent));
    }
  }, [productionSiteCalendar]);

  const handleDismissAlert = () => {
    dispatch(actions.dismissAlert())
  }

  const getEvent = (override: OverrideDate): EventInput => {
    return {
      title: override.label,
      editable: false,
      date: override.date,
      allDay: true,
      override: override
    } as EventInput;
  }

  const getApi = () => {
    const { current: calendarDom } = calendarRef;
    return calendarDom ? calendarDom.getApi() : null;
  };

  // Calendar event handler
  const handleNext = () => {
    let calendarApi = getApi();
    calendarApi?.next();
  }

  const handlePrevious = () => {
    let calendarApi = getApi();
    calendarApi?.prev();
  }

  const handleDateClick = (dateClickInfo: any) => {
    let override = getOverride(dateClickInfo.date);
    if (!override) {
      override = {
        date: moment(dateClickInfo.date).format("YYYY-MM-DD"),
        isProductionDate: !isProductionSiteWorkingDay(dateClickInfo.date),
        label: "",
      };
    }

    setEditOverride(override);
  };

  const handleEventClick = (eventClickInfo: any) => {
    eventClickInfo.jsEvent.preventDefault(); // our events don't have a URL, don't try to go there.
    setEditOverride(eventClickInfo.event.extendedProps.override as OverrideDate);
  }

  const handleWorkdayToggle = (date: Date) => {
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });

    //Assume the weekday toggle is currently on, so we need to remove the day from the list of weekdays
    let weekdays = productionSiteCalendar.productionWeekDays.filter((productionSiteDay) => productionSiteDay !== day);
    //If we didn't remove anything, it wasn't in the list, so we need to add it
    if (weekdays.length === productionSiteCalendar.productionWeekDays.length) {
      weekdays.push(day);
    }

    dispatch(actions.saveProductionSiteWeekDays(plantCode, weekdays))
  }

  const calculateBackGroundColor = (cellInfo: any): string => {
    const override = getOverride(cellInfo.date);
    if (override) {
      return override.isProductionDate ? "bg-success" : "bg-secondary"
    }
    if (isProductionSiteWorkingDay(cellInfo.date)) {
      return "bg-success"
    }
    return "bg-secondary"
  }

  const getOverride = (date: Date): OverrideDate | undefined => {
    return productionSiteCalendar?.overrideDates?.find((override) => {
      return moment(override.date).isSame(moment(date), 'day')
    })
  }

  const isProductionSiteWorkingDay = (date: Date): boolean => {
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });
    let weekdays = productionSiteCalendar?.productionWeekDays ?? [];
    return (weekdays && weekdays.find(productionSiteDay => productionSiteDay === day)) ? true : false
  }

  const makeColumnHeader = (cellInfo: any) => {
    const isWorkingDay = isProductionSiteWorkingDay(cellInfo.date);
    return (
      <>
        <div className="mb-3">{cellInfo.date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
        <button
          onClick={() => { handleWorkdayToggle(cellInfo.date) }}
          disabled={weekdaysLocked || numberOfApiCallsInProgress > 0}
          className='table-action-icons-container-button-small'
        >
          <ActiveIconWithCopy
            active={isWorkingDay}
            copy={weekdaysLocked ? 'Locked' : isWorkingDay ? 'Producing' : 'Closed'}
            customStyle='table-action-icons-children-small'
            color="#1BC5BD" //same as bg-success in FullCalendar
          />
        </button>
      </>
    )
  }

  const makeDayNumber = (cellInfo: any) => {
    const classname = cellInfo.isToday ? "font-weight-bold border border-dark rounded-circle px-1" : ""
    return (
      <div className={classname}>
        {cellInfo.date.getDate()}
      </div>
    )
  }

  const makeEventContent = (eventInfo: any) => {
    const override = eventInfo.event.extendedProps.override as OverrideDate;
    return (
      <>
        {override &&
          <div className="d-flex">
            <div className="text-danger font-weight-bold pr-1" onClick={(e) => handleDeleteOverride(override)}          >
              X
            </div>
            <OverlayTrigger
              placement='right'
              overlay={
                <Tooltip id='local-time-explanation'>
                  <div className="text-left">
                    {override.label}
                  </div>
                </Tooltip>
              }
            >
              <div>
                <div>{override.label.slice(0, 10) + (override.label.length > 10 ? "..." : "")}</div>
              </div>
            </OverlayTrigger>
          </div>}
      </>
    )
  }

  const handleDeleteOverride = (overrideToUpdate: OverrideDate) => {
    dispatch(actions.removeProductionSiteCalendarOverride(plantCode, overrideToUpdate.date));
    setEditOverride(undefined);
  }

  const handleSaveOverride = (newOverride: OverrideDate) => {
    dispatch(actions.saveProductionSiteCalendarOverride(plantCode, newOverride));
    setEditOverride(undefined);
  }

  const handleLockWeekdaysToggle = () => {
    setWeekdaysLocked(!weekdaysLocked);
  }

  //Render
  return (
    <>
      <div className="row">
        <div>
          {alert && alert?.message && (
            <Alert variant="danger" onClose={handleDismissAlert} dismissible>
              <Alert.Heading>{alert.message}</Alert.Heading>
              {alert.url && <div>{`Call to ${alert.url}`}</div>}
              {alert.apiResponseCode && <div>{`ResponseCode: ${alert.apiResponseCode}`}</div>}
              {alert.apiMessage && <div>{`Message: ${alert.apiMessage}`}</div>}
              {alert.data && <div>{`Body: ${alert.data}`}</div>}
            </Alert>
          )}
          <div className="card card-custom card-stretch gutter-b">
            <div className="card-header">
              <h3 className="card-label card-title font-weight-bolder text-dark">
                {(numberOfApiCallsInProgress > 0) &&
                  <div className='d-flex justify-content-center'>
                    <div className="overlay-layer bg-transparent">
                      <div className="spinner spinner-lg spinner-success" />
                    </div>
                  </div>
                }
              </h3>
            </div>
            <div className="card-body">
              {editOverride &&
                <OverrideDateEditModal
                  override={editOverride}
                  onCancel={() => setEditOverride(undefined)}
                  onDelete={() => handleDeleteOverride(editOverride)}
                  onSave={(override) => handleSaveOverride(override)}
                />
              }
              <div className="col-lg-6">
                <FullCalendar
                  plugins={[
                    bootstrap,
                    dayGridPlugin,
                    interactionPlugin,
                  ]}
                  customButtons={{
                    previousButton: {
                      text: "«",
                      click: handlePrevious,
                    },
                    nextButton: {
                      text: "»",
                      click: handleNext,
                    },
                  }}
                  headerToolbar={{
                    left: "previousButton",
                    center: "title",
                    right: "nextButton",
                  }}
                  aspectRatio={1.5}
                  height="auto"
                  ref={calendarRef}
                  themeSystem="bootstrap"
                  initialView="dayGridMonth"
                  eventDisplay="block"
                  firstDay={1}
                  weekends={true}
                  showNonCurrentDates={false}
                  dateClick={handleDateClick}
                  dayCellClassNames={calculateBackGroundColor}
                  dayCellContent={makeDayNumber}
                  dayHeaderContent={makeColumnHeader}
                  events={events}
                  eventContent={makeEventContent}
                  eventClick={handleEventClick}
                  eventBackgroundColor="transparent"
                  eventBorderColor="transparent"
                  eventTextColor="black"
                ></FullCalendar>
              </div>
              <div className=" col-lg-6"></div>
              <div className="d-flex justify-content-start">
                <button
                  className="btn btn-link text-primary"
                  onClick={handleLockWeekdaysToggle}
                >
                  {weekdaysLocked ? "Unlock working day toggles" : "Lock working day toggles"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { ProductionSiteCalendarPage };
